/**
 * Pure helpers for the "Attach Vehicles & Operations" compose/preview UI
 * (PRD `FeaturePlans/OpsUpload/prd-fixed-route-vehicles-and-operations.md`,
 * Phase 1 item 5 / Modals 2 & 3, GAP B/C/E/G).
 *
 * These functions are intentionally side-effect free so they can be unit tested
 * without React or any Ops client. They cover:
 *   - building a default `DraftVehicleAssignment` from a route + Ops vehicle,
 *   - day-of-week mask <-> label helpers,
 *   - expanding a draft plan into the **preview** list of shift solutions that
 *     WOULD be created (one per route version x vehicle x operating day),
 *     using the corrected `DatedShiftSolutionRequest` shape.
 *
 * IMPORTANT: this module never calls a write endpoint. The expanded requests are
 * for PREVIEW only — actual `createshiftsolution` is Phase 2.
 *
 * All date/time composition routes through `dateTimeUtils` per repo convention
 * (never `new Date("YYYY-MM-DD")` or `.toISOString().split('T')[0]`).
 */

import type {
  DraftVehicleAssignment,
  FixedRoute,
  RouteOperationAssignment,
  ServiceDaysMask,
} from '../../../types/fixedRoutes';
import type { DatedShiftSolutionRequest } from '../../../services/fixedRouteUatClient';
import type { OpsVehicle } from '../../../services/fixedRoute/vehicleMappingTypes';
import { combineDateWithTime, extractServiceDate } from '../../../utils/dateTimeUtils';

// ─── Day-of-week helpers ───────────────────────────────────────────────────────

/** Ordered Mon–Sun keys of a {@link ServiceDaysMask}. */
export const SERVICE_DAY_KEYS: ReadonlyArray<keyof ServiceDaysMask> = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

/** Short labels for each day, aligned 1:1 with {@link SERVICE_DAY_KEYS}. */
export const SERVICE_DAY_SHORT_LABELS: Readonly<Record<keyof ServiceDaysMask, string>> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun',
};

/** A mask with every day disabled. */
export function emptyServiceDaysMask(): ServiceDaysMask {
  return {
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
  };
}

/** A mask with Mon–Fri enabled (the common default when no calendar exists). */
export function weekdayServiceDaysMask(): ServiceDaysMask {
  return {
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: false,
    sunday: false,
  };
}

/** Count of enabled days in a mask. */
export function countServiceDays(mask: ServiceDaysMask): number {
  return SERVICE_DAY_KEYS.reduce((n, key) => (mask[key] ? n + 1 : n), 0);
}

/** Compact human label for a mask, e.g. "Mon, Wed, Fri" or "Every day". */
export function describeServiceDays(mask: ServiceDaysMask): string {
  const enabled = SERVICE_DAY_KEYS.filter((key) => mask[key]);
  if (enabled.length === 0) return 'No days selected';
  if (enabled.length === SERVICE_DAY_KEYS.length) return 'Every day';
  return enabled.map((key) => SERVICE_DAY_SHORT_LABELS[key]).join(', ');
}

// ─── Default assignment construction ───────────────────────────────────────────

/** Whether a string looks like a valid `HH:mm` wall-clock time. */
function isHHmm(value: string | undefined | null): value is string {
  return typeof value === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(value.trim());
}

/**
 * Builds a sensible default {@link DraftVehicleAssignment} for a route + Ops
 * vehicle. Time window defaults from the route timetable; day mask defaults from
 * the route's GTFS-derived `serviceDays` (Agent A), falling back to Mon–Fri.
 */
export function buildDefaultVehicleAssignment(
  route: Pick<FixedRoute, 'startTime' | 'endTime' | 'serviceDays' | 'isCirculator' | 'vehicleCount'>,
  opsVehicle: Pick<OpsVehicle, 'id'>,
  cartographerVehicleId?: string,
): DraftVehicleAssignment {
  const startTime = isHHmm(route.startTime) ? route.startTime.trim() : '08:00';
  const endTime = isHHmm(route.endTime) ? (route.endTime as string).trim() : '18:00';
  const serviceDays = route.serviceDays ? { ...route.serviceDays } : weekdayServiceDaysMask();

  return {
    opsVehicleId: opsVehicle.id,
    cartographerVehicleId,
    direction: 'forward',
    startStopIndex: 0,
    firstStopOffsetMins: 0,
    numberOfRepeats: 0,
    shiftType: 'timetable',
    startTime,
    endTime,
    serviceDays,
  };
}

// ─── Preview expansion ─────────────────────────────────────────────────────────

/**
 * A single previewed shift solution row: the request that WOULD be sent for one
 * (route version x vehicle x operating day), plus display context. Never sent.
 */
export interface PreviewShiftSolution {
  /** Stable key for React lists. */
  key: string;
  routeId: string;
  routeName: string;
  /** Ops route version id, if the route has been uploaded; undefined otherwise. */
  routeVersionId: string | undefined;
  operationSolutionId: string;
  operationName?: string;
  opsVehicleId: string;
  /** The operating day this row represents (label only — see notes). */
  dayLabel: string;
  /** The corrected dated-shift-solution request shape (PREVIEW only). */
  request: DatedShiftSolutionRequest;
}

export interface ExpandPreviewOptions {
  /**
   * Plan START date anchor (`YYYY-MM-DD`) — composes each shift's `startDate`.
   * Defaults to the operation's `dateRange.startDate`, then the route service
   * date range start.
   */
  serviceDate?: string;
  /**
   * Plan END date anchor (`YYYY-MM-DD`) — composes each shift's `endDate`.
   * Defaults to the operation's `dateRange.endDate`, then the route service date
   * range end, then the start anchor (single-day plan).
   */
  endDate?: string;
}

/**
 * The effective per-assignment day mask: a per-vehicle `serviceDays` override if
 * present, else the route-level mask, else Mon–Fri.
 */
export function effectiveServiceDays(
  route: Pick<FixedRoute, 'serviceDays'>,
  assignment: Pick<DraftVehicleAssignment, 'serviceDays'>,
): ServiceDaysMask {
  if (assignment.serviceDays) return assignment.serviceDays;
  if (route.serviceDays) return route.serviceDays;
  return weekdayServiceDaysMask();
}

/**
 * Expands a route's draft `operationAssignments` into the flat preview list of
 * shift solutions that would be created — one per (operation x vehicle x
 * operating day in the effective mask).
 *
 * The `startDate`/`endDate` on each request are composed via
 * `combineDateWithTime` against the resolved service-date anchor, so the wall
 * time is preserved regardless of the viewer's timezone (repo convention). The
 * per-day breakdown is represented one row per enabled day; the Hailer model
 * itself only carries a contiguous range (PRD GAP B), so the day label is the
 * Phase-1 staging representation of "which days this runs".
 */
export function expandDraftToPreview(
  route: FixedRoute,
  options: ExpandPreviewOptions = {},
): PreviewShiftSolution[] {
  const assignments = route.operationAssignments ?? [];
  if (assignments.length === 0) return [];

  const routeVersionId = route.uatRouteId || route.uatMetadata?.originalId;
  const rows: PreviewShiftSolution[] = [];

  for (const operation of assignments) {
    // Plan date range, applied to every vehicle/shift in this operation.
    // Priority: operation.dateRange (set in the modal) → options → route GTFS
    // range. End falls back to start (single-day). Avoid `new Date()` (repo rule).
    const startRaw =
      operation.dateRange?.startDate || options.serviceDate || route.serviceDateRange?.startDate;
    const endRaw =
      operation.dateRange?.endDate ||
      options.endDate ||
      route.serviceDateRange?.endDate ||
      startRaw;
    const startAnchor = startRaw ? extractServiceDate(startRaw) : undefined;
    const endAnchor = endRaw ? extractServiceDate(endRaw) : startAnchor;

    for (const assignment of operation.vehicleAssignments) {
      const mask = effectiveServiceDays(route, assignment);
      const enabledDays = SERVICE_DAY_KEYS.filter((key) => mask[key]);
      const days = enabledDays.length > 0 ? enabledDays : ([null] as Array<keyof ServiceDaysMask | null>);

      for (const dayKey of days) {
        // Every enabled day shares the SAME plan-level start/end anchors. The
        // per-day rows differ only by `dayLabel`, so `collapsePreviewRows` merges
        // them into one row listing the days (e.g. "Mon, Wed, Fri") over the plan
        // date range. The Hailer model carries no per-day distinction (PRD GAP B).
        const request = buildPreviewRequest(
          operation,
          assignment,
          routeVersionId,
          startAnchor,
          endAnchor,
        );
        rows.push({
          key: `${operation.operationSolutionId}|${assignment.opsVehicleId}|${dayKey ?? 'range'}`,
          routeId: route.id,
          routeName: route.name,
          routeVersionId,
          operationSolutionId: operation.operationSolutionId,
          operationName: operation.operationName,
          opsVehicleId: assignment.opsVehicleId,
          dayLabel: dayKey ? SERVICE_DAY_SHORT_LABELS[dayKey] : 'Date range',
          request,
        });
      }
    }
  }

  return rows;
}

/**
 * Builds the corrected {@link DatedShiftSolutionRequest} for one assignment.
 * `startDate`/`endDate` are full wall-time datetimes when a service-date anchor
 * is available, else the bare anchor (or empty). Never sent — preview only.
 */
export function buildPreviewRequest(
  operation: Pick<RouteOperationAssignment, 'operationSolutionId'>,
  assignment: DraftVehicleAssignment,
  routeVersionId: string | undefined,
  startAnchor: string | undefined,
  endAnchor: string | undefined = startAnchor,
): DatedShiftSolutionRequest {
  const startDate =
    startAnchor && isHHmm(assignment.startTime)
      ? combineDateWithTime(startAnchor, assignment.startTime)
      : startAnchor ?? '';
  const endDate =
    endAnchor && isHHmm(assignment.endTime)
      ? combineDateWithTime(endAnchor, assignment.endTime)
      : endAnchor ?? '';

  return {
    operationSolutionId: operation.operationSolutionId,
    routeVersionId: routeVersionId ?? '',
    direction: assignment.direction,
    startDate,
    endDate,
    numberOfRepeats: assignment.numberOfRepeats,
    startStopIndex: assignment.startStopIndex,
    shiftType: assignment.shiftType,
    firstStopOffsetMins: assignment.firstStopOffsetMins,
    vehicleId: assignment.opsVehicleId || undefined,
    driverId: assignment.driverId || undefined,
  };
}

/** Total shift-solution rows the current draft would produce (for summaries). */
export function countPreviewRows(route: FixedRoute, options?: ExpandPreviewOptions): number {
  return expandDraftToPreview(route, options).length;
}

// ─── Collapsed preview (group days onto one line) ───────────────────────────────

/**
 * A preview row with the operating DAYS collapsed onto a single line. Rows that
 * are identical in everything but the day (same route, vehicle, operation, times,
 * direction, type, repeats, indices) merge into one entry whose `daysLabel` lists
 * the days — e.g. "Mon, Wed, Fri". This mirrors the Hailer model, where the
 * shift-solution request itself carries no per-day distinction (PRD GAP B), so
 * the per-day rows are a display nicety, not distinct requests.
 */
export interface CollapsedPreviewRow {
  key: string;
  routeId: string;
  routeName: string;
  routeVersionId: string | undefined;
  operationSolutionId: string;
  operationName?: string;
  opsVehicleId: string;
  /** "Mon, Wed, Fri" | "Every day" | "Date range". */
  daysLabel: string;
  request: DatedShiftSolutionRequest;
}

const SHORT_LABEL_ORDER: Record<string, number> = SERVICE_DAY_KEYS.reduce(
  (acc, key, i) => {
    acc[SERVICE_DAY_SHORT_LABELS[key]] = i;
    return acc;
  },
  {} as Record<string, number>,
);

/**
 * Collapse {@link expandDraftToPreview} output so each distinct shift solution is
 * one row with its days listed. Grouping key = everything that defines the shift
 * solution except the day. Insertion order is preserved.
 */
export function collapsePreviewRows(rows: PreviewShiftSolution[]): CollapsedPreviewRow[] {
  const groups = new Map<string, { sig: string; row: PreviewShiftSolution; days: string[] }>();
  for (const row of rows) {
    const r = row.request;
    const sig = [
      row.routeId,
      row.operationSolutionId,
      row.opsVehicleId,
      r.direction,
      r.shiftType,
      r.startDate,
      r.endDate,
      r.numberOfRepeats,
      r.startStopIndex,
      r.firstStopOffsetMins,
    ].join('|');
    const existing = groups.get(sig);
    if (existing) {
      if (!existing.days.includes(row.dayLabel)) existing.days.push(row.dayLabel);
    } else {
      groups.set(sig, { sig, row, days: [row.dayLabel] });
    }
  }

  // Array.from over the Map values is safe across transpile targets (a raw
  // `for...of` over a Map can no-op under ES5-without-downlevelIteration).
  return Array.from(groups.values()).map(({ sig, row, days }) => {
    const ordered = days
      .slice()
      .sort((a, b) => (SHORT_LABEL_ORDER[a] ?? 99) - (SHORT_LABEL_ORDER[b] ?? 99));
    const isEveryDay = SERVICE_DAY_KEYS.every((k) => ordered.includes(SERVICE_DAY_SHORT_LABELS[k]));
    const daysLabel = ordered.includes('Date range')
      ? 'Date range'
      : isEveryDay
        ? 'Every day'
        : ordered.join(', ');
    return {
      key: sig,
      routeId: row.routeId,
      routeName: row.routeName,
      routeVersionId: row.routeVersionId,
      operationSolutionId: row.operationSolutionId,
      operationName: row.operationName,
      opsVehicleId: row.opsVehicleId,
      daysLabel,
      request: row.request,
    };
  });
}
